import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import AdminLayout from "@/components/feature/AdminLayout";
import { projectDetails, fallbackProject } from "@/mocks/admin/projects";
import DetailHeader from "./components/DetailHeader";
import CustomerCard from "./components/CustomerCard";
import AddressCard from "./components/AddressCard";
import CrewCard from "./components/CrewCard";
import ItemsList from "./components/ItemsList";
import Checklist from "./components/Checklist";
import PricingCard from "./components/PricingCard";
import Timeline from "./components/Timeline";
import ContactModal from "./components/ContactModal";
import MessageHistory from "./components/MessageHistory";
import CollectBalanceModal from "./components/CollectBalanceModal";

type ContactTarget = {
  audience: "customer" | "crew";
  name: string;
  email?: string;
  phone?: string;
};

export default function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const base = (id && projectDetails[id]) || fallbackProject;
  const project = { ...base, id: id || base.id };

  const [contact, setContact] = useState<ContactTarget | null>(null);
  const [historyKey, setHistoryKey] = useState(0);
  const [collectOpen, setCollectOpen] = useState(false);
  const [collectedExtra, setCollectedExtra] = useState(0);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const openCustomerContact = () => {
    setContact({
      audience: "customer",
      name: project.customer.name,
      email: project.customer.email,
      phone: project.customer.phone,
    });
  };

  const openCrewContact = (crew: typeof project.crew[number]) => {
    setContact({
      audience: "crew",
      name: crew.name,
      phone: crew.phone,
    });
  };

  const handleCollected = (amount: number) => {
    setCollectedExtra((prev) => prev + amount);
    setToast(`$${amount.toLocaleString()} balance recorded for ${project.customer.name.split(" ")[0]}`);
  };

  return (
    <AdminLayout>
      <div className="px-4 md:px-8 py-6 md:py-8 max-w-[1600px] mx-auto space-y-6">
        <DetailHeader project={project} onSendText={openCustomerContact} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
          {/* Main column */}
          <div className="lg:col-span-2 space-y-4 md:space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
              <CustomerCard
                customer={project.customer}
                onMessage={openCustomerContact}
              />
              <AddressCard
                from={project.fromAddress}
                to={project.toAddress}
                distance={project.distanceMiles}
              />
            </div>

            <ItemsList items={project.items} />
            <Checklist initial={project.checklist} />
            <MessageHistory projectId={project.id} refreshKey={historyKey} />
            <Timeline timeline={project.timeline} notes={project.notes} />
          </div>

          {/* Side column */}
          <div className="space-y-4 md:space-y-5">
            <CrewCard
              crew={project.crew}
              truck={project.truck}
              onMessageCrew={openCrewContact}
            />
            <PricingCard
              pricing={project.pricing}
              extraPaid={collectedExtra}
              onCollect={() => setCollectOpen(true)}
            />

            {/* Quick actions card */}
            <div className="bg-gradient-to-br from-ink-900 to-ink-700 rounded-lg p-5 text-white">
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-flashlight-line text-brand-300"></i>
                <span className="text-sm font-bold">Quick Actions</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button className="text-xs font-semibold bg-white/10 hover:bg-white/15 transition-colors py-2 rounded-md cursor-pointer whitespace-nowrap">
                  <i className="ri-calendar-event-line mr-1"></i>
                  Reschedule
                </button>
                <button
                  onClick={openCustomerContact}
                  className="text-xs font-semibold bg-white/10 hover:bg-white/15 transition-colors py-2 rounded-md cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-message-2-line mr-1"></i>
                  Message
                </button>
                <button className="text-xs font-semibold bg-white/10 hover:bg-white/15 transition-colors py-2 rounded-md cursor-pointer whitespace-nowrap">
                  <i className="ri-file-copy-line mr-1"></i>
                  Duplicate
                </button>
                <button className="text-xs font-semibold bg-white/10 hover:bg-white/15 transition-colors py-2 rounded-md cursor-pointer whitespace-nowrap">
                  <i className="ri-close-circle-line mr-1"></i>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ContactModal
        open={!!contact}
        onClose={() => {
          setContact(null);
          setHistoryKey((k) => k + 1);
        }}
        audience={contact?.audience || "customer"}
        recipient={{
          name: contact?.name || "",
          email: contact?.email,
          phone: contact?.phone,
        }}
        projectId={project.id}
        moveType={project.moveType}
        date={project.scheduledStart}
      />

      <CollectBalanceModal
        open={collectOpen}
        onClose={() => setCollectOpen(false)}
        projectId={project.id}
        customer={{
          name: project.customer.name,
          initials: project.customer.initials,
          color: project.customer.avatarColor,
        }}
        balanceDue={Math.max(0, project.pricing.balanceDue - collectedExtra)}
        onRecorded={handleCollected}
      />

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 bg-ink-900 text-white rounded-lg px-4 py-3 shadow-lg flex items-center gap-3 max-w-sm animate-[fadeIn_0.2s_ease-out]">
          <span className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-300">
            <i className="ri-check-double-line"></i>
          </span>
          <div className="text-sm">
            <div className="font-bold">Payment recorded</div>
            <div className="text-white/70 text-xs mt-0.5">{toast}</div>
          </div>
          <button
            onClick={() => setToast(null)}
            className="text-white/60 hover:text-white cursor-pointer"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}
    </AdminLayout>
  );
}